import type { OutcomePageContent } from "./types";

/**
 * Outcome pages. They start from the problem a buyer owns and only then name
 * products, so the same page works for someone who has never heard of Cerberus.
 *
 * Every capability claim here has to be true of a shipping product — these
 * pages summarize the product pages rather than promising beyond them. Named
 * regulations are cited with their adopted numbering; nothing on these pages
 * claims a certification Odingard holds on a customer's behalf.
 */

const GOVERN_AI: OutcomePageContent = {
  path: "/govern-ai",
  kind: "solution",
  eyebrow: "Solution",
  headline:
    "You cannot govern an AI estate you have never inventoried — start by finding out what is already running",
  lede:
    "Employees, applications and agents adopted AI before anyone wrote the policy. Governing that means discovering what is in use, deciding what it is allowed to touch, and keeping the record current as it changes.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=govern-ai" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  challenge: {
    heading: "Adoption already happened",
    body:
      "The question is no longer whether the organization uses AI. It is which models, in which systems, holding which data — and who approved it. Most governance programs start by discovering that nobody knows.",
    points: [
      {
        title: "Shadow adoption",
        body: "Models and AI features arrive inside tools you already bought, so nothing triggers a review.",
      },
      {
        title: "Policy without enforcement",
        body: "A published acceptable-use policy is not a control. Nothing in the runtime knows it exists.",
      },
      {
        title: "The inventory goes stale immediately",
        body: "A spreadsheet of AI systems is accurate on the day it is written and wrong by the next release.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Discover the AI already in use",
        body: "TraceLock's AI inventory tracks discovered models and AI services through their lifecycle instead of relying on teams to self-report.",
      },
      {
        title: "Attach each system to an obligation",
        body: "Inventory entries map to the controls and framework requirements that apply to them, so governance has a subject rather than a principle.",
      },
      {
        title: "Constrain what agents can reach",
        body: "Cerberus judges the tool call, not the prompt: privileged reads, untrusted content and outbound actions are correlated and the action is interrupted before it executes.",
      },
    ],
  },
  drivers: {
    heading: "What is driving the deadline",
    items: [
      {
        title: "EU AI Act (Regulation 2024/1689)",
        body: "Obligations phase in by risk classification, and classification depends on knowing which systems you operate.",
      },
      {
        title: "ISO/IEC 42001:2023",
        body: "An AI management system is auditable, and the inventory of AI systems is one of the first things an auditor asks for.",
      },
      {
        title: "NIST AI RMF 1.0",
        body: "The Map function is explicitly about establishing context and inventory before risk can be measured or managed.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["tracelock", "cerberus"],
  },
  services: {
    heading: "Have Warden run the discovery",
    body:
      "Warden inventories the estate, classifies systems against the frameworks that apply, and stands up the governance program around them.",
    links: [
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
      { label: "AI Governance Advisory", href: "/warden/advisory" },
      { label: "Virtual AI Risk Officer", href: "/warden/virtual-airo" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Prove AI Compliance", href: "/ai-compliance" },
      { kind: "Product", label: "TraceLock — continuous control monitoring", href: "/tracelock" },
      { kind: "Pillar", label: "Governance & Compliance", href: "/governance" },
      { kind: "Trust", label: "Odingard Trust Center", href: "https://trust.odingard.com", external: true },
    ],
  },
  close: {
    heading: "Start with the inventory",
    body:
      "Everything else in an AI governance program depends on knowing what is running. That is the first engagement, and it is short.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=govern-ai" },
      { label: "Explore Warden", href: "/warden" },
    ],
  },
};

const SECURE_AGENTIC_AI: OutcomePageContent = {
  path: "/secure-agentic-ai",
  kind: "solution",
  eyebrow: "Solution",
  headline:
    "Any agent that can read your data, read the web and send a message is already exploitable",
  lede:
    "The attack needs no zero-day and no malware — three tool calls the agent was designed to make, and an instruction hidden in content it was told to read. Defending it means judging the action, not the text.",
  ctas: [
    { label: "Explore Cerberus", href: "/cerberus" },
    { label: "Read the research", href: "/research" },
  ],
  challenge: {
    heading: "Autonomy removed the human checkpoint",
    body:
      "Most security controls quietly assume a person reviews the consequential action. An agent does not wait, and the exfiltration looks exactly like the work it was asked to do.",
    points: [
      {
        title: "Prompt filtering guesses at intent",
        body: "Text classification loses across languages, encodings and paraphrase. The outbound call is the thing worth judging.",
      },
      {
        title: "Model hardening is not a control",
        body: "Model-level resistance shifts which payloads work; it does not remove the condition that makes them possible.",
      },
      {
        title: "Poisoned memory outlives the page",
        body: "An instruction written into memory keeps working long after the injected content is gone, and the transcript looks clean.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Block the trifecta at the tool boundary",
        body: "Cerberus correlates sensitive data access, untrusted content and outbound intent across the session, and holds the guarded call when they close.",
      },
      {
        title: "Attack your own agents first",
        body: "Argus red-teams autonomous systems the way an adversary would, so exposure is found by you and not disclosed to you.",
      },
      {
        title: "Detect memory poisoning",
        body: "Mimir seeds decoys that reveal when an agent's memory has been tampered with. It is in preview and labeled as such.",
      },
      {
        title: "Replay any verdict",
        body: "Each decision carries the signals and contamination graph that produced it, so an incident review is a query rather than an excavation.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    lede: "Start with the MIT-licensed Cerberus core in your own environment; the rest layers on top.",
    slugs: ["cerberus", "argus", "mimir"],
  },
  services: {
    heading: "Deploy it with the engineers who built it",
    body:
      "Warden stands the runtime up alongside your team, reviews the boundaries you are enforcing, and red-teams the agents behind them.",
    links: [
      { label: "Cerberus Runtime Deployment", href: "/warden/cerberus-deployment" },
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Research", label: "Lethal Trifecta validation report", href: "/research" },
      { kind: "Demo", label: "Watch a live attack get blocked", href: "/demos" },
      { kind: "Pillar", label: "AI Security", href: "/ai-security" },
      { kind: "Documentation", label: "Integration guide", href: "/docs" },
    ],
  },
  close: {
    heading: "Give agents real access without the risk",
    body:
      "The core is MIT licensed and installs in your own environment. The evidence set behind it is published in full.",
    ctas: [
      { label: "Explore Cerberus", href: "/cerberus" },
      { label: "Request a briefing", href: "/contact?topic=secure-agentic-ai" },
    ],
  },
};

const AUDIT_READY: OutcomePageContent = {
  path: "/audit-ready",
  kind: "solution",
  eyebrow: "Solution",
  headline:
    "Replace the annual evidence scramble with controls that are tested while you are not looking",
  lede:
    "Point-in-time evidence describes a system that has since changed. Continuous control monitoring tests the live environment on a schedule and files the result where an auditor can verify it.",
  ctas: [
    { label: "Explore TraceLock", href: "/tracelock" },
    { label: "See the frameworks", href: "/tracelock#evidence" },
  ],
  challenge: {
    heading: "The evidence pack is rebuilt from scratch every year",
    body:
      "Screenshots are collected under deadline, the same control is re-tested once per framework, and nobody can prove the artifact was not edited after collection.",
    points: [
      {
        title: "Stale by the time it is filed",
        body: "Evidence proves what was true on the day someone had time to gather it.",
      },
      {
        title: "Duplicated per framework",
        body: "Access review, logging and change management get evaluated separately for each report instead of once.",
      },
      {
        title: "Unverifiable custody",
        body: "If an artifact can be changed after the fact, the auditor is trusting your word rather than checking a record.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Evaluate once, report against every framework",
        body: "One unified control library crosswalks to SOC 2, ISO 27001, NIST CSF 2.0, NIST AI RMF, ISO/IEC 42001, the EU AI Act, the HIPAA Security Rule, SEC rules and CIS Controls v8, with fifteen regulated framework packs on top.",
      },
      {
        title: "Test the real environment",
        body: "AWS, GCP, GitHub, Cloudflare and custody connectors raise findings against the controls they cover, on a schedule, without a questionnaire.",
      },
      {
        title: "Hand auditors verifiable evidence",
        body: "Artifacts are recorded write-once with a SHA-256 digest, so custody can be checked rather than asserted.",
      },
      {
        title: "Give the auditor a room, not your drive",
        body: "Scoped, revocable, read-only access to exactly the evidence in scope — no shared folders and no standing accounts.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["tracelock", "sentience"],
  },
  services: {
    heading: "Run it as a managed program",
    body:
      "Warden builds the crosswalk out to your control set, operates evidence collection, and manages the auditor room through the examination.",
    links: [
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
      { label: "Evidence Operations", href: "/warden/evidence-operations" },
      { label: "Auditor Room Management", href: "/warden/auditor-room" },
      { label: "Control Crosswalk Build-out", href: "/warden/crosswalk" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Product", label: "TraceLock — the crosswalk and connectors", href: "/tracelock" },
      { kind: "Solution", label: "Prove AI Compliance", href: "/ai-compliance" },
      { kind: "Solution", label: "Manage Third-Party Risk", href: "/third-party-risk" },
      { kind: "Trust", label: "Odingard Trust Center", href: "https://trust.odingard.com", external: true },
    ],
  },
  close: {
    heading: "Stop rebuilding the same evidence pack",
    body:
      "See the control library and the connectors running against your own environment before you commit to a program.",
    ctas: [
      { label: "Explore TraceLock", href: "/tracelock" },
      { label: "Request a briefing", href: "/contact?topic=audit-ready" },
    ],
  },
};

const THIRD_PARTY_RISK: OutcomePageContent = {
  path: "/third-party-risk",
  kind: "solution",
  eyebrow: "Solution",
  headline:
    "The questionnaire tells you what a vendor claimed on the day they answered it",
  lede:
    "Third-party risk fails in the gap between onboarding and the next review. Shorten the questionnaire, then keep watching the posture that actually changes.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=third-party-risk" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  challenge: {
    heading: "Assessment is a snapshot of a moving target",
    body:
      "A vendor is assessed once, filed, and re-examined a year later. In between, their architecture changed, they adopted AI, and nobody told you.",
    points: [
      {
        title: "Questionnaires take weeks",
        body: "The same evidence is requested in a slightly different format by every customer, and answering it is a full-time job on both sides.",
      },
      {
        title: "No signal between reviews",
        body: "Nothing watches the vendor's posture once the assessment is filed.",
      },
      {
        title: "Their AI is now your exposure",
        body: "A vendor adding an AI feature changes where your data goes, without changing the contract you assessed.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Cut questionnaire turnaround",
        body: "Answer from an evidence library that is already current, rather than assembling a fresh pack per requester.",
      },
      {
        title: "Publish posture once",
        body: "A Trust Center gives prospects and customers a self-service view of controls and evidence instead of an email thread.",
      },
      {
        title: "Track vendor AI as inventory",
        body: "The AI systems a vendor introduces are recorded against the obligations they affect, not discovered at renewal.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["tracelock"],
  },
  services: {
    heading: "Have Warden operate the program",
    body:
      "Warden builds the assessment workflow, runs evidence operations, and keeps the crosswalk current as your vendor obligations change.",
    links: [
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
      { label: "Evidence Operations", href: "/warden/evidence-operations" },
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
      { kind: "Product", label: "TraceLock — continuous control evidence", href: "/tracelock" },
      { kind: "Trust", label: "Odingard Trust Center", href: "https://trust.odingard.com", external: true },
    ],
  },
  close: {
    heading: "Assess faster, then keep watching",
    body:
      "The onboarding assessment is the easy half. The value is in what you know about the vendor six months later.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=third-party-risk" },
      { label: "Explore TraceLock", href: "/tracelock" },
    ],
  },
};

const AI_COMPLIANCE: OutcomePageContent = {
  path: "/ai-compliance",
  kind: "solution",
  eyebrow: "Solution",
  headline:
    "Map to the EU AI Act, ISO 42001 and NIST AI RMF once, and keep the mapping current",
  lede:
    "AI regulation arrived before most AI estates were documented. Proving compliance means classifying the systems you run, mapping them to the obligations that apply, and holding evidence that survives review.",
  ctas: [
    { label: "Explore TraceLock", href: "/tracelock" },
    { label: "Request a briefing", href: "/contact?topic=ai-compliance" },
  ],
  challenge: {
    heading: "The obligations landed faster than the inventory",
    body:
      "Classification depends on knowing which systems exist and what they do. Most organizations are asked to demonstrate control over an estate they have not yet cataloged.",
    points: [
      {
        title: "Classification without an inventory",
        body: "Risk tiering assumes you can enumerate your AI systems and their purposes.",
      },
      {
        title: "Three frameworks, one estate",
        body: "The EU AI Act, ISO 42001 and NIST AI RMF overlap heavily, but each is usually answered from scratch.",
      },
      {
        title: "Evidence that has to hold up",
        body: "An AI governance claim is only as good as the record behind it, and screenshots are not a record.",
      },
    ],
  },
  outcomes: {
    heading: "With Odingard, you can",
    items: [
      {
        title: "Crosswalk the overlap instead of repeating it",
        body: "One control evaluation maps through to every framework it defensibly supports, including the AI-specific ones.",
      },
      {
        title: "Keep the AI inventory attached to obligations",
        body: "Discovered models and services are tracked through their lifecycle with the requirements that apply to them.",
      },
      {
        title: "Show the boundary is enforced, not just documented",
        body: "Cerberus produces runtime evidence that agents were actually constrained, which is a stronger answer than a written control.",
      },
    ],
  },
  drivers: {
    heading: "The frameworks in scope",
    lede: "Named as adopted. Mappings are curated at clause level and reviewed before they are relied on as an audit deliverable.",
    items: [
      {
        title: "EU AI Act — Regulation (EU) 2024/1689",
        body: "Risk-tiered obligations for providers and deployers, phasing in on the adopted timetable.",
      },
      {
        title: "ISO/IEC 42001:2023",
        body: "A certifiable AI management system standard, structured like ISO 27001 and audited the same way.",
      },
      {
        title: "NIST AI RMF 1.0",
        body: "Govern, Map, Measure and Manage — voluntary, and increasingly written into contracts as if it were not.",
      },
    ],
  },
  products: {
    heading: "What delivers it",
    slugs: ["tracelock", "cerberus"],
  },
  services: {
    heading: "Have Warden get you ready",
    body:
      "Warden classifies the estate, builds the mappings out to your controls, and briefs the board or the regulator on where you stand.",
    links: [
      { label: "EU AI Act Readiness", href: "/warden/eu-ai-act" },
      { label: "ISO 42001 Alignment", href: "/warden/iso-42001" },
      { label: "NIST AI RMF Alignment", href: "/warden/nist-ai-rmf" },
      { label: "Board & Regulator Briefings", href: "/warden/briefings" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Govern AI Adoption", href: "/govern-ai" },
      { kind: "Solution", label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
      { kind: "Product", label: "TraceLock — clause-level framework mapping", href: "/tracelock" },
      { kind: "Pillar", label: "Governance & Compliance", href: "/governance" },
    ],
  },
  close: {
    heading: "Answer three frameworks with one program",
    body:
      "See the crosswalk against your own control set, or have Warden run the readiness assessment first.",
    ctas: [
      { label: "Explore TraceLock", href: "/tracelock" },
      { label: "Request a briefing", href: "/contact?topic=ai-compliance" },
    ],
  },
};

export const SOLUTION_PAGES: OutcomePageContent[] = [
  GOVERN_AI,
  SECURE_AGENTIC_AI,
  AUDIT_READY,
  THIRD_PARTY_RISK,
  AI_COMPLIANCE,
];
